import get from 'lodash/get';
import extend from 'lodash/extend';
import capitalize from 'lodash/capitalize';

import identityConfig from '../../../../config/identity';
import { figureOutPriorityTag } from '../../../../utils/ad_util';

export default function omniture(context, location, firstLoad, url, isFBIA) {
  const siteConfig = identityConfig;
  const disableOmniture = get(context, 'config.navHistory[0]')
    ? get(context, 'config.navHistory[0]').indexOf('?omniture=false') !== -1
    : false;

  if (siteConfig === undefined || disableOmniture) {
    return '';
  }
  const omnitureConfig = get(siteConfig, 'omniture');
  const siteSlug = get(siteConfig, 'slug');

  const {
    postData,
    tagPageData,
    categoryPageData,
    authorPageData,
    searchPageData,
    staticPageData,
    mostCreativePeoplePageData,
    personPageData,
    statusCode,
    ibdPageData,
    companyPageData,
    micYearPageData,
    micSectorPageData,
    videoPageData,
    productPageData,
    playlistAllFeedData,
    type
  } = context;
  let category = '';
  let tag = '';

  // Omniture data structure, defaults to homepage value
  // it will be further extended depend on page type
  let omnitureMapping = {
    server: isFBIA ? `${siteSlug} FBIA` : siteSlug,
    channel: 'homepage',
    pageName: `${siteSlug}:homepage:front`,
    events: 'event9',
    prop1: '',
    prop2: '',
    prop3: '',
    prop4: get(siteConfig, 'url'),
    prop5: '',
    prop6: '',
    prop7: '',
    prop8: '',
    prop9: '',
    prop10: '',
    prop11: '',
    prop13: 1,
    prop21: 'anonymous',
    prop22: `anonymous:${siteSlug}:homepage:front`,
    prop23: 'anonymous',
    prop28: '',
    prop31: '',
    prop32: '',
    prop33: '',
    prop34: '',
    eVar49: ''
  };

  const typeLookup = {
    post() {
      category = get(postData, 'categories[0].slug');
      const postID = get(postData, 'id');
      const postSlug = get(postData, 'slug');
      const tags = get(postData, 'tags');
      const eyeBrowText = figureOutPriorityTag(tags);

      let pageType = 'article';
      if (get(postData, 'posterSlideshow.length')) {
        pageType = 'slideshow';
      } else if (
        get(postData, 'hasVideoPoster') === true ||
        get(postData, 'posterVideo.feedId')
      ) {
        pageType = 'video';
      }

      return extend({}, omnitureMapping, {
        pageName: `${siteSlug}:${postID}:${postSlug}`,
        channel: '',
        prop1: category,
        prop2: category,
        prop3: get(postData, 'title'),
        prop4: get(postData, 'link'),
        prop5:
          get(eyeBrowText, 'slug') !== '' ? get(eyeBrowText, 'slug') : 'misc',
        prop6:
          get(eyeBrowText, 'slug') !== '' ? get(eyeBrowText, 'slug') : 'misc',
        prop8: get(postData, 'author.name'),
        prop9: get(postData, 'omnitureDate'),
        prop10: postID,
        prop11: get(postData, 'author.name'),
        prop22: `anonymous:${siteSlug}:${postID}:${postSlug}`,
        prop28: isFBIA ? `fbia_${pageType}` : pageType,
        prop33: capitalize(category),
        prop34: get(postData, 'readTime')
      });
    },
    category() {
      category = get(categoryPageData, 'slug', 'news');
      return extend({}, omnitureMapping, {
        channel: category,
        pageName: `${siteSlug}:${category}:front`,
        prop4: `${get(siteConfig, 'url')}/${category}`,
        prop22: `anonymous:${siteSlug}:${category}:front`
      });
    },
    tag() {
      tag = get(tagPageData, 'slug', 'tag');
      return extend({}, omnitureMapping, {
        channel: tag,
        pageName: `${siteSlug}:taxonomy:${tag}`,
        prop3: get(tagPageData, 'name'),
        prop4: `${get(siteConfig, 'url')}/section/${tag}`,
        prop5: tag,
        prop6: tag,
        prop8: 'Fast Company Contributor',
        prop11: 'Fast Company Contributor',
        prop22: `anonymous:${siteSlug}:taxonomy:${tag}`,
        prop28: 'collection'
      });
    },
    author() {
      const authorSlug = get(authorPageData, 'slug', 'fastcompany-staff');
      const authorName = get(authorPageData, 'name', 'Fastcompany Staff');

      return extend({}, omnitureMapping, {
        channel: '',
        pageName: `${siteSlug}:user:${authorSlug}`,
        prop3: authorName,
        prop4: `${get(siteConfig, 'url')}/user/${authorSlug}`,
        prop8: authorName,
        prop11: authorName,
        prop22: `anonymous:${siteSlug}:user:${authorSlug}`,
        prop28: 'user'
      });
    },
    search() {
      const searchQuery = get(searchPageData, 'query.queryString', '');
      return extend({}, omnitureMapping, {
        channel: '',
        pageName: `${siteSlug}:search:${searchQuery}`,
        prop3: searchQuery,
        prop4: `${get(siteConfig, 'url')}/search/${searchQuery}`,
        prop5: searchQuery,
        prop6: searchQuery,
        prop22: `anonymous:${siteSlug}:search:${searchQuery}`,
        prop28: 'search'
      });
    },
    page() {
      return extend({}, omnitureMapping, {
        channel: '',
        pageName: `${siteSlug}:page:${get(staticPageData, 'slug')}`,
        prop4: `${get(siteConfig, 'url')}/${get(staticPageData, 'slug')}`,
        prop22: `anonymous:${siteSlug}:page:${get(staticPageData, 'slug')}`,
        prop28: 'page'
      });
    },
    sitemapIndexPage() {
      return extend({}, omnitureMapping, {
        channel: 'sitemap',
        pageName: `${siteSlug}:sitemapIndexPage`,
        prop4: `${get(siteConfig, 'url')}/sitemap`,
        prop22: `anonymous:${siteSlug}:page:sitemapIndexPage`,
        prop28: 'sitemap'
      });
    },
    sitemapPostPage() {
      return extend({}, omnitureMapping, {
        channel: 'sitemap',
        pageName: `${siteSlug}:sitemapPostPage`,
        prop4: `${get(siteConfig, 'url')}/sitemap/posts`,
        prop22: `anonymous:${siteSlug}:page:sitemapPostPage`,
        prop28: 'sitemap'
      });
    },
    personPage() {
      const personSlug = get(personPageData, 'data.rows[0].url', '');
      return extend({}, omnitureMapping, {
        channel: 'person',
        pageName: `${siteSlug}:person:${personSlug}`,
        prop3: personSlug,
        prop4: `${get(siteConfig, 'url')}/person/${personSlug}`,
        prop22: `anonymous:${siteSlug}:person:${personSlug}`,
        prop28: 'article'
      });
    },
    personIndexSectionPage() {
      return extend({}, omnitureMapping, {
        channel: 'person',
        pageName: `${siteSlug}:person-index`,
        prop3: 'Person Index',
        prop4: `${get(siteConfig, 'url')}/person`,
        prop22: `anonymous:${siteSlug}:person-index`,
        prop28: 'person'
      });
    },
    mostCreativePeoplePage() {
      const year = get(mostCreativePeoplePageData, 'year', '');
      return extend({}, omnitureMapping, {
        channel: 'MCP',
        pageName: year ? `${siteSlug}:mcp:${year}` : `${siteSlug}:mcp:front`, // front of year
        prop3: `MCP LIST ${year}`,
        prop4: year
          ? `${get(siteConfig, 'url')}/most-creative-people/${year}`
          : `${get(siteConfig, 'url')}/most-creative-people`,
        prop22: `anonymous:${siteSlug}:page:mcp:${year}`,
        prop28: 'mcp'
      });
    },
    mostCreativePeopleCommunityPage() {
      return extend({}, omnitureMapping, {
        channel: 'MCP',
        pageName: `${siteSlug}:most-creative-people-community`,
        prop3: 'Most Creative People Community Page',
        prop4: `${get(siteConfig, 'url')}/most-creative-people-community`,
        prop22: `anonymous:${siteSlug}:mcp:community`,
        prop28: 'mcp'
      });
    },
    ibdPage() {
      const year = get(ibdPageData, 'year', '');
      return extend({}, omnitureMapping, {
        channel: 'IBD',
        pageName: year ? `${siteSlug}:ibd:${year}` : `${siteSlug}:ibd:front`, // front of year
        prop3: `Innovation By Design Awards ${year}`,
        prop4: year
          ? `${get(siteConfig, 'url')}/innovation-by-design/${year}`
          : `${get(siteConfig, 'url')}/innovation-by-design`,
        prop22: `anonymous:${siteSlug}:page:ibd`,
        prop28: 'ibd'
      });
    },
    productIndexSectionPage() {
      return extend({}, omnitureMapping, {
        channel: 'product',
        pageName: 'product:homepage:front', // front of year
        prop3: `Product Index Page`,
        prop4: 'https://www.fastcompany.com/stuff',
        prop22: 'anonymous:product:homepage:front',
        prop28: 'product'
      });
    },
    productIndexPage() {
      return extend({}, omnitureMapping, {
        channel: 'product',
        pageName: 'product:homepage:front', // front of year
        prop3: `Product Index Page`,
        prop4: 'https://www.fastcompany.com/stuff',
        prop22: 'anonymous:product:homepage:front',
        prop28: 'product'
      });
    },
    productPage() {
      const productName = get(productPageData, 'data.rows[0].name');
      const productSlug = get(productPageData, 'data.rows[0].url');
      return extend({}, omnitureMapping, {
        channel: 'product',
        pageName: `product:${productSlug}`, // front of year
        prop3: `Product: ${productName}`,
        prop4: `${get(siteConfig, 'url')}/product/${productSlug}`,
        prop22: `anonymous:product:${productSlug}`,
        prop28: 'product'
      });
    },
    companyPage() {
      const companyName = get(companyPageData, 'data.tags[0].slug', '');

      return extend({}, omnitureMapping, {
        channel: 'company',
        pageName: `company:profile:${companyName}`, // front of year
        prop3: `Company Profile Page: ${companyName}`,
        prop4: `${get(siteConfig, 'url')}/company/${companyName}`,
        prop22: `anonymous:company:profile:${companyName}`,
        prop28: 'company'
      });
    },
    companyIndexSectionPage() {
      return extend({}, omnitureMapping, {
        channel: 'company',
        pageName: 'company:index',
        prop3: `Company Index Page`,
        prop4: `${get(siteConfig, 'url')}/company`,
        prop22: `anonymous:company:index`,
        prop28: 'company'
      });
    },
    micYearPage() {
      const year = get(micYearPageData, 'year', '');
      return extend({}, omnitureMapping, {
        channel: 'mic',
        pageName: year ? `${siteSlug}:mic:${year}` : `${siteSlug}:mic:front`, // front of year
        prop3: `Most Innovative Companies ${year}`,
        prop4: year
          ? `${get(siteConfig, 'url')}/most-innovative-companies/${year}`
          : `${get(siteConfig, 'url')}/most-innovative-companies`,
        prop22: `anonymous:${siteSlug}:mic`,
        prop28: 'mic'
      });
    },
    micSectorPage() {
      const year = get(micSectorPageData, 'year', '');
      const sector = get(micSectorPageData, 'sector', '');

      return extend({}, omnitureMapping, {
        channel: 'mic',
        pageName: `${siteSlug}:mic:${year}:${sector}`,
        prop3: `Most Innovative Companies ${year} ${sector}`,
        prop4: `${get(
          siteConfig,
          'url'
        )}/most-innovative-companies/${year}/${sector}`,
        prop22: `anonymous:${siteSlug}:mic:${year}:${sector}`,
        prop28: 'mic'
      });
    },
    videoPage() {
      const currentVideoData = get(videoPageData, 'data.playlist[0]', null);
      if (!currentVideoData) {
        return {};
      }

      const videoTag = get(currentVideoData, 'category');
      const postID = get(currentVideoData, 'id');

      // Extend original omniture variable
      return extend({}, omnitureMapping, {
        pageName: `${siteSlug}:${postID}:${get(currentVideoData, 'slug')}`,
        channel: 'video',
        prop1: 'video',
        prop2: 'video',
        prop3: get(currentVideoData, 'title'),
        prop4: get(currentVideoData, 'link'),
        prop5: videoTag,
        prop6: videoTag,
        prop8: 'FastCo Studios',
        prop9: get(currentVideoData, 'omnitureDate'),
        prop10: postID,
        prop11: 'FastCo Studios',
        prop22: `anonymous:${siteSlug}:${postID}:${get(
          currentVideoData,
          'slug'
        )}`,
        prop28: 'video',
        prop33: capitalize('video')
      });
    },
    videoIndexPage() {
      return extend({}, omnitureMapping, {
        channel: 'video',
        pageName: `${siteSlug}:video:front`,
        prop4: `${get(siteConfig, 'url')}/videos`,
        prop22: `anonymous:${siteSlug}:video:front`
      });
    },
    videoPlaylistPage() {
      const feedid = get(playlistAllFeedData, 'data.feedid');
      const slug = get(playlistAllFeedData, 'data.slug');
      return extend({}, omnitureMapping, {
        channel: 'video',
        pageName: `fastcompany:video-playlist:${slug}`,
        pageURL: `https://www.fastcompany.com/videos/playlist/${slug}/${feedid}`,
        url: `https://www.fastcompany.com/videos/playlist/${slug}/${feedid}`,
        prop1: '',
        prop2: '',
        prop3: '',
        prop4: `https://www.fastcompany.com/videos/playlist/${slug}/${feedid}`,
        prop5: '',
        prop6: '',
        prop7: '',
        prop8: '',
        prop9: '',
        prop10: '',
        prop11: '',
        prop22: `anonymous:fastcompany:video-playlist:${slug}`,
        prop28: '',
        prop31: '',
        prop32: '',
        prop33: '',
        prop34: ''
      });
    },
    magazineArchivePage() {
      return extend({}, omnitureMapping, {
        channel: 'magazine',
        pageName: 'magazine:magazine-archives:front', // front of year
        prop3: `Magazine Archives Page`,
        prop4: `${get(siteConfig, 'url')}/magazine/archives`,
        prop22: 'anonymous:magazine:archives:front',
        prop28: 'magazine'
      });
    },
    newslettersPreferencesPage() {
      return extend({}, omnitureMapping, {
        channel: 'newsletters',
        pageName: 'newsletters:newsletters-preferences:front', // front of year
        prop3: `Fast Company Newsletters Preferences Page`,
        prop4: `https://www.fastcompany.com/newsletters-preferences`,
        prop22: 'anonymous:newsletters:newsletters-preferences:front',
        prop28: 'newsletters'
      });
    }
  };

  if (statusCode && statusCode === 404) {
    omnitureMapping = extend({}, omnitureMapping, {
      channel: 'Error Page',
      pageType: 'errorPage',
      pageName: '',
      events: '',
      prop1: '',
      prop2: '',
      prop3: '',
      prop4: get(siteConfig, 'url'),
      prop5: '',
      prop6: '',
      prop7: '',
      prop8: '',
      prop9: '',
      prop10: '',
      prop11: '',
      prop13: 1,
      prop21: '',
      prop22: '',
      prop23: '',
      prop31: true,
      prop32: 404,
      prop33: '',
      prop34: ''
    });
  }

  if (typeLookup[type]) {
    omnitureMapping = typeLookup[type]();
  }

  return `
    <script type="text/javascript">
    (function (global, oDOC, handler) {
    var head = oDOC.head || oDOC.getElementsByTagName("head");
    function LABjsLoaded() {
        window.fastco_s = window.fastco_s || {};
        window.fastco_s.server="${get(omnitureMapping, 'server')}";
        window.fastco_s.channel="${get(omnitureMapping, 'channel')}";
        window.fastco_s.pageName="${get(omnitureMapping, 'pageName')}";
        window.fastco_s.events="${get(omnitureMapping, 'events')}";
        window.fastco_s.prop1="${get(omnitureMapping, 'prop1')}";
        window.fastco_s.prop2="${get(omnitureMapping, 'prop2')}";
        window.fastco_s.prop3="${get(omnitureMapping, 'prop3')}";
        window.fastco_s.prop4="${get(omnitureMapping, 'prop4')}";
        window.fastco_s.prop5="${get(omnitureMapping, 'prop5')}";
        window.fastco_s.prop6="${get(omnitureMapping, 'prop6')}";
        window.fastco_s.prop7="${get(omnitureMapping, 'prop7')}";
        window.fastco_s.prop8="${get(omnitureMapping, 'prop8')}";
        window.fastco_s.prop9="${get(omnitureMapping, 'prop9')}";
        window.fastco_s.prop10="${get(omnitureMapping, 'prop10')}";
        window.fastco_s.prop11="${get(omnitureMapping, 'prop11')}";
        window.fastco_s.prop13="${get(omnitureMapping, 'prop13')}";
        window.fastco_s.prop21="${get(omnitureMapping, 'prop21')}";
        window.fastco_s.prop22="${get(omnitureMapping, 'prop22')}";
        window.fastco_s.prop23="${get(omnitureMapping, 'prop23')}";
        window.fastco_s.prop28="${get(omnitureMapping, 'prop28')}";
        window.fastco_s.prop31="${get(omnitureMapping, 'prop31')}";
        window.fastco_s.prop32="${get(omnitureMapping, 'prop32')}";
        window.fastco_s.prop33="${get(omnitureMapping, 'prop33')}";
        window.fastco_s.eVar49=document.cookie
        .split('; ')
        .filter(c => /abgroup=/.test(c))[0] && document.cookie
        .split('; ')
        .filter(c => /abgroup=/.test(c))[0].includes('abgroup=') ? document.cookie
        .split('; ')
        .filter(c => /abgroup=/.test(c))[0]
        .toString()
        .replace('abgroup=', '') : undefined;
        window.s_code = fastco_s.t();
        if (s_code)document.write(s_code)
      }
      // loading code borrowed directly from LABjs itself
    setTimeout(function () {
        if ("item" in head) { // check if ref is still a live node list
            if (!head[0]) { // append_to node not yet ready
                setTimeout(arguments.callee, 25);
                return;
            }
            head = head[0]; // reassign from live node list ref to pure node ref -- avoids nasty IE bug where changes to DOM invalidate live node lists
        }
        var scriptElem = oDOC.createElement("script"),
            scriptdone = false;
        scriptElem.onload = scriptElem.onreadystatechange = function () {
            if ((scriptElem.readyState && scriptElem.readyState !== "complete" && scriptElem.readyState !== "loaded") || scriptdone) {
                return false;
            }
            scriptElem.onload = scriptElem.onreadystatechange = null;
            scriptdone = true;
            LABjsLoaded();
        };
        scriptElem.src = "${get(omnitureConfig, 'scode')}";
        head.insertBefore(scriptElem, head.firstChild);
    }, 0);

    // required: shim for FF <= 3.5 not having document.readyState
    if (oDOC.readyState == null && oDOC.addEventListener) {
        oDOC.readyState = "loading";
        oDOC.addEventListener("DOMContentLoaded", handler = function () {
            oDOC.removeEventListener("DOMContentLoaded", handler, false);
            oDOC.readyState = "complete";
        }, false);
    }
})(window, document);
    </script>
    <script type="text/javascript"><!--
    if(navigator.appVersion.indexOf('MSIE')>=0)document.write(unescape('%3C')+'\\!-'+'-')
    //--></script><noscript><img src="${omnitureConfig.trackingPixel}"
    height="1" width="1" alt="" style="border:0;" /></noscript><!--/DO NOT REMOVE/-->
  `;
}
